import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, Stack, TextField, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import ScienceIcon from '@mui/icons-material/Science';
import AddIcon from '@mui/icons-material/Add';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { api } from '@/api/client';
import { formatDateTime } from '@/utils/format';
import type { LabOrder } from '@/types/medical';

export function ClinicianLabOrdersPage() {
  const [params] = useSearchParams();
  const initialAppointment = params.get('appointment') ?? '';
  const [open, setOpen] = useState(false);

  const q = useApiQuery({
    queryKey: ['clinician', 'lab-orders'],
    queryFn:  () => api.get<LabOrder[]>('/api/medical/clinician/lab-orders/'),
  });

  return (
    <>
      <PageHeader
        title="Lab orders"
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setOpen(true)}>
            New lab order
          </Button>
        }
      />

      <DataState query={q} empty={{ title: 'No lab orders yet' }}>
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((o) => <Row key={o.id} order={o} />)}
          </Stack>
        )}
      </DataState>

      {open && (
        <BuilderDialog open={open}
                       appointmentId={initialAppointment || null}
                       onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function Row({ order }: { order: LabOrder }) {
  const colorMap: Record<LabOrder['status'], 'default' | 'primary' | 'success'> = {
    draft: 'default', issued: 'primary',
    collected: 'primary', in_progress: 'primary',
    reported: 'success', cancelled: 'default',
  };
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <ScienceIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {order.panel_name || `Lab order #${order.id}`}
              </Typography>
              <Chip
                size="small" variant="outlined"
                color={colorMap[order.status]}
                label={order.status.replace(/_/g, ' ')}
                sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Patient #{order.patient} · {order.test_codes?.length ?? 0} test(s)
              {order.indication_icd_code && ` · ICD ${order.indication_icd_code}`}
            </Typography>
          </Box>
          {order.issued_at && (
            <Typography variant="caption" color="text.secondary" className="tabular">
              issued {formatDateTime(order.issued_at)}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function BuilderDialog({
  open, appointmentId, onClose,
}: {
  open: boolean;
  appointmentId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [patientId, setPatientId] = useState('');
  const [panelName, setPanelName] = useState('');
  const [testCodes, setTestCodes] = useState('');
  const [icd, setIcd] = useState('');
  const [instructions, setInstructions] = useState('');

  const issueMut = useApiMutation({
    mutationFn: () => api.post<LabOrder>('/api/medical/clinician/lab-orders/', {
      patient_id: parseInt(patientId, 10),
      appointment_id: appointmentId ?? null,
      panel_name: panelName,
      test_codes: testCodes.split(',').map((s) => s.trim()).filter(Boolean),
      indication_icd_code: icd,
      instructions,
    }),
    onSuccess: () => {
      toast.success('Lab order issued.');
      qc.invalidateQueries({ queryKey: ['clinician', 'lab-orders'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = patientId && panelName && testCodes.trim().length > 0;

  return (
    <Dialog open={open} onClose={() => !issueMut.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>New lab order</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="Patient ID" required
              value={patientId}
              onChange={(e) => setPatientId(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="Panel name" required
              value={panelName} onChange={(e) => setPanelName(e.target.value)}
              placeholder="Basic metabolic panel"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Test codes" required
              placeholder="LOINC, comma-separated. e.g. 33747-0, 2160-0, 2345-7"
              value={testCodes} onChange={(e) => setTestCodes(e.target.value)}
              helperText="Use LOINC codes when known."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="Indication ICD"
              value={icd} onChange={(e) => setIcd(e.target.value)}
              placeholder="E11.9"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={2} label="Instructions for patient"
              value={instructions} onChange={(e) => setInstructions(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={issueMut.isPending}>Cancel</Button>
        <Button
          variant="contained" disabled={!valid || issueMut.isPending}
          onClick={() => issueMut.mutate()}
        >
          Issue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
